/**
 * Members tab — list, invite, role change, revoke.
 */
import { useState, useEffect } from 'react';
import { api } from '../../../api';
import ReasonField from '../../../components/ops_admin/ReasonField';
import SigninLinkPanel from '../../../components/ops_admin/SigninLinkPanel';
import MemberAccountModal from '../../../components/ops_admin/MemberAccountModal';
import RekeyPanel from '../../../components/ops_admin/RekeyPanel';

export default function OrgMembersTab({ org, opsRole }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showRekeyAll, setShowRekeyAll] = useState(false);
  const canMutate = ['admin', 'owner'].includes(opsRole);

  async function fetchMembers() {
    setLoading(true);
    try {
      const d = await api.get(`/ops_admin/orgs/${org.id}/members`);
      setMembers(d.members || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { fetchMembers(); }, [org.id]);

  if (loading) return <p className="loading-text">Loading members...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="ops-tab">
      <div className="ops-page-header">
        <h2>Members ({members.length})</h2>
        {canMutate && (
          <div className="button-row">
            {opsRole === 'owner' && (
              <button className="btn btn-ghost" onClick={() => setShowRekeyAll(v => !v)}
                title="Cloud Identity: rewrite Firebase-era uids to Directory ids for every member of this org">
                Re-key by email
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setShowInvite(true)}>+ Invite Member</button>
          </div>
        )}
      </div>

      {showRekeyAll && (
        <RekeyPanel orgId={org.id} onClose={() => setShowRekeyAll(false)} onApplied={fetchMembers} />
      )}

      <table className="ops-table">
        <thead>
          <tr><th>Email</th><th>Display Name</th><th>Role</th><th>Joined</th>{canMutate && <th>Actions</th>}</tr>
        </thead>
        <tbody>
          {members.length === 0 && <tr><td colSpan={5} className="empty-row">No members</td></tr>}
          {members.map(m => (
            <MemberRow
              key={m.id}
              member={m}
              orgId={org.id}
              canMutate={canMutate}
              onRefresh={fetchMembers}
              redact={!!org.is_criminal_justice}
            />
          ))}
        </tbody>
      </table>

      {showInvite && (
        <InviteModal orgId={org.id} onClose={() => setShowInvite(false)} onCreated={() => { setShowInvite(false); fetchMembers(); }} />
      )}
    </div>
  );
}

function MemberRow({ member, orgId, canMutate, onRefresh, redact }) {
  const [editing, setEditing] = useState(false);
  const [newRole, setNewRole] = useState(member.role);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [resendResult, setResendResult] = useState(null);
  const [resending, setResending] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showRekey, setShowRekey] = useState(false);

  async function handleRoleChange() {
    setSaving(true);
    try {
      await api.patch(`/ops_admin/orgs/${orgId}/members/${member.id}`, { role: newRole, reason });
      setEditing(false);
      setReason('');
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleRevoke() {
    if (!reason.trim()) { setError('Reason required'); return; }
    setSaving(true);
    try {
      await api.del(`/ops_admin/orgs/${orgId}/members/${member.id}?reason=${encodeURIComponent(reason)}`);
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleResend() {
    setResending(true);
    setError(null);
    try {
      const r = await api.post(`/ops_admin/orgs/${orgId}/members/${member.id}/resend_link`);
      setResendResult(r);
    } catch (e) { setError(e.message); } finally { setResending(false); }
  }

  return (
    <>
      <tr>
        <td>
          {redact
            ? <span className="mono" title="Redacted (criminal-justice mode)">{member.user_id.slice(0, 8)}</span>
            : <>
                {member.email}
                {member.identity_email && member.identity_email !== member.email && (
                  <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted, #6c757d)' }} title="Sign-in username">
                    {member.identity_email}
                  </div>
                )}
              </>}
        </td>
        <td>{redact ? <span className="redacted">— redacted —</span> : member.display_name}</td>
        <td>
          {editing ? (
            <select className="form-input form-input-sm" value={newRole} onChange={e => setNewRole(e.target.value)}>
              {['admin', 'attorney', 'paralegal', 'viewer'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          ) : member.role}
        </td>
        <td>{member.created_at ? new Date(member.created_at).toLocaleDateString() : '—'}</td>
        {canMutate && (
          <td>
            {editing ? (
              <div>
                <ReasonField value={reason} onChange={setReason} placeholder="Reason..." />
                {error && <p className="error-text">{error}</p>}
                <button className="btn btn-sm btn-primary" onClick={handleRoleChange} disabled={saving}>Save</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn btn-sm btn-danger" onClick={handleRevoke} disabled={saving}>Revoke</button>
              </div>
            ) : (
              <div className="member-actions">
                <button className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>Edit role</button>
                <button className="btn btn-sm btn-ghost" onClick={handleResend} disabled={resending}>
                  {resending ? 'Generating...' : 'Resend link'}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setShowAccount(true)}>
                  Account
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => setShowRekey(v => !v)}
                  title="Cloud Identity: rewrite this member's Firebase-era uid to their Directory id">
                  Re-key
                </button>
                {error && <p className="error-text">{error}</p>}
              </div>
            )}
          </td>
        )}
      </tr>
      {showRekey && (
        <tr>
          <td colSpan={5}>
            <RekeyPanel orgId={orgId} member={member} onClose={() => setShowRekey(false)} onApplied={onRefresh} />
          </td>
        </tr>
      )}
      {showAccount && (
        <tr>
          <td colSpan={5}>
            <MemberAccountModal
              member={member}
              orgId={orgId}
              onClose={() => setShowAccount(false)}
              onChanged={onRefresh}
            />
          </td>
        </tr>
      )}
      {resendResult?.setup_link && (
        <tr>
          <td colSpan={5}>
            <div className="modal-overlay">
              <div className="modal">
                <h2>{resendResult.has_password ? 'Password reset' : 'New setup link'} — {resendResult.email}</h2>
                <SigninLinkPanel
                  email={resendResult.email}
                  setupLink={resendResult.setup_link}
                  firebaseUserCreated={false}
                  hasPassword={resendResult.has_password}
                  identityEmail={resendResult.identity_email}
                  oneTimePassword={resendResult.one_time_password}
                  authProvider={resendResult.auth_provider}
                  emailSent={resendResult.kind === 'link' ? !!resendResult.email_sent : undefined}
                  onDone={() => setResendResult(null)}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function InviteModal({ orgId, onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', role: 'attorney', display_name: '', reason: '', send_email: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const res = await api.post(`/ops_admin/orgs/${orgId}/members`, form);
      setResult(res);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  if (result?.member) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h2>Member added — {result.member?.email}</h2>
          <SigninLinkPanel
            email={result.member?.email}
            setupLink={result.setup_link}
            firebaseUserCreated={result.firebase_user_created}
            hasPassword={result.has_password}
            identityEmail={result.identity_email}
            authProvider={result.auth_provider}
            emailSent={result.setup_link ? !!result.email_sent : undefined}
            onDone={() => { setResult(null); onCreated(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Invite Member</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" required value={form.email}
              onChange={e => setForm(f => ({...f, email: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-input" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
              {['admin', 'attorney', 'paralegal', 'viewer'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Display Name *</label>
            <input className="form-input" required value={form.display_name}
              onChange={e => setForm(f => ({...f, display_name: e.target.value}))} placeholder="First Last" />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input type="checkbox" checked={form.send_email}
                onChange={e => setForm(f => ({...f, send_email: e.target.checked}))} />
              Email them the invitation (their login ID, the button, the steps with pictures, the quick start guide)
            </label>
            {!form.send_email && (
              <p className="signin-link-hint" style={{ marginTop: 4 }}>
                You'll get the link to send yourself.
              </p>
            )}
          </div>
          <ReasonField value={form.reason} onChange={r => setForm(f => ({...f, reason: r}))} />
          {error && <p className="error-text">{error}</p>}
          <div className="button-row">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Inviting...' : (form.send_email ? 'Send invitation' : 'Create link')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
